import React from 'react';
import { Clock, CheckCircle, Banknote, CheckCircle2, Lock, Building2, Tag, Undo2, PencilLine, ShieldCheck, ShieldAlert, AlertTriangle, MinusCircle, PackagePlus, PackageOpen } from 'lucide-react';

// Badge das 4 fases do ciclo do Troco, compartilhado entre Controle, Depósito e
// Confirmação. Mantém cor + rótulo consistentes em todas as telas.
// STATUS 'Aprovado' é exibido como "Recebido" (a loja recebeu o troco) — o nome
// interno foi mantido por compatibilidade com os dados de produção.
const CONFIG = {
    Pendente:   { rotulo: 'Pendente',   bg: '#fef3c7', cor: '#d97706', Icone: Clock },
    Aprovado:   { rotulo: 'Recebido',   bg: '#dbeafe', cor: '#2563eb', Icone: CheckCircle },
    Depositado: { rotulo: 'Depositado', bg: '#ede9fe', cor: '#7c3aed', Icone: Banknote },
    Finalizado: { rotulo: 'Finalizado', bg: '#dcfce7', cor: '#16a34a', Icone: CheckCircle2 },
};
// A tela de Saldos rende PELO MESMO badge os dois vocabulários de status: o do
// troco (dtbTroco: Pendente→Aprovado→Depositado→Finalizado) e o do movimento
// (dtbTrocoMovimento: Depositado→Confirmado). Um depósito só aparece no extrato
// de Saldos quando 'Confirmado' — o estado terminal do dinheiro, o mesmo "fim de
// ciclo" que Finalizado no troco. Sem este alias, 'Confirmado' não tinha chave e
// caía no fallback 'Pendente', pintando de âmbar um depósito já finalizado.
CONFIG.Confirmado = CONFIG.Finalizado;

export const TrocoStatusBadge = ({ status }) => {
    const c = CONFIG[status] || CONFIG.Pendente;
    const { Icone } = c;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '4px 10px', borderRadius: '999px',
            backgroundColor: c.bg, color: c.cor,
            fontSize: '11px', fontWeight: '700', textTransform: 'uppercase',
        }}>
            <Icone size={12} /> {c.rotulo}
        </span>
    );
};

// ============================================================================
// Destino do depósito (etapa 3) — para onde o dinheiro do troco foi
// ============================================================================
// Cofre      → guardado no cofre da loja, dentro da "banana" (malote) — tem número.
// Escritório → enviado ao escritório em vez do cofre; NÃO existe banana, então não
//              há número a informar (o destino ocupa o lugar do documento na tela).
// Os valores gravados no banco vão SEM acento ('Escritorio'); o acento é só rótulo.
export const TROCO_DESTINO = { COFRE: 'Cofre', ESCRITORIO: 'Escritorio' };

const DESTINO_CONFIG = {
    Cofre:      { rotulo: 'Cofre',      bg: '#f1f5f9', cor: '#475569', Icone: Lock },
    Escritorio: { rotulo: 'Escritório', bg: '#e0f2fe', cor: '#0369a1', Icone: Building2 },
};

// Rótulo de tela do destino. Linha antiga (depositada antes de 2026-07-14) não tem
// destino gravado — naquela época só existia o cofre, então cai no 'Cofre'.
export const rotuloDestino = (destino) => (DESTINO_CONFIG[destino] || DESTINO_CONFIG.Cofre).rotulo;

// ============================================================================
// Tipo do lançamento — nem tudo na dtbTroco é troco (migration 2026-07-28)
// ============================================================================
// Troco     → ciclo normal de 4 etapas (o padrão; toda linha antiga é isso).
// Devolucao → a loja devolveu numerário excedente ao escritório (dinheiro ANDOU).
// Correcao  → acerto do saldo da loja, SEM dinheiro se movendo.
// Gravado sem acento no banco ('Devolucao'/'Correcao'); o acento é só rótulo.
// ⚠️ O tipo EXPLICA a linha — não a tira do saldo. Os dois acertos continuam
// somando/abatendo no ledger, senão o saldo da loja mentiria.
export const TROCO_TIPO = { TROCO: 'Troco', DEVOLUCAO: 'Devolucao', CORRECAO: 'Correcao' };

const TIPO_CONFIG = {
    Troco:     { rotulo: 'Troco',      curto: 'Troco',     bg: '#f1f5f9', cor: '#475569', Icone: Tag },
    Devolucao: { rotulo: 'Devolução ao escritório', curto: 'Devolução', bg: '#ffedd5', cor: '#c2410c', Icone: Undo2 },
    Correcao:  { rotulo: 'Correção de saldo',       curto: 'Correção',  bg: '#e0e7ff', cor: '#4338ca', Icone: PencilLine },
};

// Rótulo de tela do tipo. Linha anterior à migration não tem TIPO gravado — na
// época só existia o ciclo de troco, então cai no 'Troco'.
export const rotuloTipo = (tipo, curto = false) => {
    const c = TIPO_CONFIG[tipo] || TIPO_CONFIG.Troco;
    return curto ? c.curto : c.rotulo;
};

export const ehAjuste = (tipo) => tipo === TROCO_TIPO.DEVOLUCAO || tipo === TROCO_TIPO.CORRECAO;

// Badge do tipo. Só aparece no acerto: marcar toda linha de troco com "Troco"
// seria ruído — o normal não precisa de etiqueta, a exceção precisa.
export const TrocoTipoBadge = ({ tipo, curto = true }) => {
    if (!ehAjuste(tipo)) return null;
    const c = TIPO_CONFIG[tipo];
    const { Icone } = c;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '3px 8px', borderRadius: '999px',
            backgroundColor: c.bg, color: c.cor,
            fontSize: '10px', fontWeight: '700', textTransform: 'uppercase',
        }}>
            <Icone size={11} /> {curto ? c.curto : c.rotulo}
        </span>
    );
};

export const TrocoDestinoBadge = ({ destino }) => {
    const c = DESTINO_CONFIG[destino] || DESTINO_CONFIG.Cofre;
    const { Icone } = c;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '4px 10px', borderRadius: '999px',
            backgroundColor: c.bg, color: c.cor,
            fontSize: '11px', fontWeight: '700', textTransform: 'uppercase',
        }}>
            <Icone size={12} /> {c.rotulo}
        </span>
    );
};

// ============================================================================
// TIPO DE OPERAÇÃO — o vocabulário do relatório de papel (2026-07-30)
// ============================================================================
// A tela de Controle passou a falar a língua do "Relatório de troco" que a
// operação já usa impresso. O SINAL de cada tipo vem de lá:
//   Lançamento de Troco     → POSITIVO  (o escritório mandou dinheiro à loja)
//   Depósito de Troco       → NEGATIVO  (a loja mandou ao cofre)
//   Devolução ao Escritório → NEGATIVO  (a loja devolveu o excedente)
//   Correção                → dos dois lados (4º tipo, discreto)
// Os valores espelham OPERACAO do server/financeiro/troco-utils.js.
export const OPERACAO = {
    LANCAMENTO: 'LancamentoTroco',
    DEPOSITO: 'DepositoTroco',
    DEVOLUCAO: 'DevolucaoEscritorio',
    CORRECAO: 'Correcao',
};

const OPERACAO_CONFIG = {
    LancamentoTroco:     { rotulo: 'Lançamento de Troco',     bg: '#dbeafe', cor: '#1d4ed8', Icone: PackagePlus },
    DepositoTroco:       { rotulo: 'Depósito de Troco',       bg: '#ede9fe', cor: '#6d28d9', Icone: Banknote },
    DevolucaoEscritorio: { rotulo: 'Devolução ao Escritório', bg: '#ffedd5', cor: '#c2410c', Icone: Undo2 },
    Correcao:            { rotulo: 'Correção',                bg: '#e0e7ff', cor: '#4338ca', Icone: PencilLine },
};

export const rotuloOperacao = (operacao) => (OPERACAO_CONFIG[operacao] || {}).rotulo || operacao;

export const TrocoOperacaoBadge = ({ operacao }) => {
    const c = OPERACAO_CONFIG[operacao];
    if (!c) return null;
    const { Icone } = c;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '4px 10px', borderRadius: '999px',
            backgroundColor: c.bg, color: c.cor,
            fontSize: '11px', fontWeight: '700',
            whiteSpace: 'nowrap',
        }}>
            <Icone size={12} /> {c.rotulo}
        </span>
    );
};

// ============================================================================
// FASE do ciclo — os rótulos do fluxo (Envio → ... → Recebimento do Escritório)
// ============================================================================
// ⚠️ POR QUE ISTO NÃO É O TrocoStatusBadge ACIMA:
//   O Controle fala a língua do FLUXO ("Recebido pela Loja", "Recebimento do
//   Escritório"), que é o vocabulário que a operação usa. As telas antigas
//   (Depósito, Confirmação) usam rótulos curtos em colunas estreitas e morrem na
//   Fase 4 — trocar os rótulos delas agora só arriscaria quebrar layout de tela
//   que está de saída. Os dois convivem até lá.
//
// Cobre os dois vocabulários de status, porque a lista mistura as duas tabelas:
//   dtbTroco          → Pendente | Aprovado | Depositado | Finalizado
//   dtbTrocoMovimento → Depositado | Confirmado
const FASE_CONFIG = {
    Pendente:   { rotulo: 'Pendente',                  bg: '#fef3c7', cor: '#b45309', Icone: Clock },
    Aprovado:   { rotulo: 'Recebido pela Loja',        bg: '#dbeafe', cor: '#1d4ed8', Icone: CheckCircle },
    Depositado: { rotulo: 'Depósito',                  bg: '#ede9fe', cor: '#6d28d9', Icone: Banknote },
    Finalizado: { rotulo: 'Recebimento do Escritório', bg: '#dcfce7', cor: '#15803d', Icone: CheckCircle2 },
    Confirmado: { rotulo: 'Recebimento do Escritório', bg: '#dcfce7', cor: '#15803d', Icone: CheckCircle2 },
};

// Rótulo de tela da fase. Exportado porque a ordenação da coluna "Situação" no
// Controle precisa ordenar pelo TEXTO QUE APARECE, não pelo código do banco:
// alfabeticamente `Aprovado` viria antes de `Depositado`, mas na tela o usuário
// lê "Recebido pela Loja" e "Depósito" — e esperaria D antes de R.
export const rotuloFase = (status) => (FASE_CONFIG[status] || {}).rotulo || status || '';

export const TrocoFaseBadge = ({ status }) => {
    const c = FASE_CONFIG[status];
    if (!c) return null;
    const { Icone } = c;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '4px 10px', borderRadius: '999px',
            backgroundColor: c.bg, color: c.cor,
            fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap',
        }}>
            <Icone size={12} /> {c.rotulo}
        </span>
    );
};

// ============================================================================
// SITUAÇÃO NA CONFERÊNCIA — as 3 caixas da tela (2026-07-30)
// ============================================================================
// A Conferência nasceu com QUATRO blocos de cards e o Financeiro achou confuso:
// era um fluxo só, partido em quatro lugares, exigindo navegação para entender
// o que precisava de ação. Virou UMA TABELA com três situações — o mesmo
// formato do Controle, que eles já aprovaram.
//
//   AguardandoConfirmacao → precisa do FINANCEIRO. Junta duas coisas que a tela
//       antiga separava sem motivo: o depósito que o caixa registrou e o motor
//       casou (proposta) e o depósito que a loja já declarou. Nos dois casos a
//       pergunta é a mesma — "o dinheiro chegou?" — e quem responde é o mesmo.
//   AguardandoDeposito   → precisa da LOJA. Troco recebido e ainda não depositado.
//   DepositoEmAberto     → o caixa registrou um depósito e NÃO há troco em aberto
//       para casar. Não é acusação: quase sempre é troco que a loja ainda não
//       confirmou ter recebido. Fica visível para alguém decidir.
export const SITUACAO = {
    CONFIRMACAO: 'AguardandoConfirmacao',
    DEPOSITO: 'AguardandoDeposito',
    EM_ABERTO: 'DepositoEmAberto',
};

const SITUACAO_CONFIG = {
    AguardandoConfirmacao: {
        rotulo: 'Aguardando Confirmação com Financeiro', curto: 'Aguardando Financeiro',
        bg: '#fef3c7', cor: '#b45309', Icone: Clock,
    },
    AguardandoDeposito: {
        rotulo: 'Aguardando Depósito', curto: 'Aguardando Depósito',
        bg: '#dbeafe', cor: '#1d4ed8', Icone: PackageOpen,
    },
    DepositoEmAberto: {
        rotulo: 'Depósito em Aberto', curto: 'Depósito em Aberto',
        bg: '#ffedd5', cor: '#c2410c', Icone: AlertTriangle,
    },
};

export const rotuloSituacao = (s, curto = false) => {
    const c = SITUACAO_CONFIG[s];
    if (!c) return s || '';
    return curto ? c.curto : c.rotulo;
};

export const TrocoSituacaoBadge = ({ situacao, curto = true }) => {
    const c = SITUACAO_CONFIG[situacao];
    if (!c) return null;
    const { Icone } = c;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            padding: '4px 10px', borderRadius: '999px',
            backgroundColor: c.bg, color: c.cor,
            fontSize: '11px', fontWeight: '700', whiteSpace: 'nowrap',
        }}>
            <Icone size={12} /> {curto ? c.curto : c.rotulo}
        </span>
    );
};

// ============================================================================
// Conferência com o PDV — o carimbo do depósito
// ============================================================================
// Mesmo veredito da aba "Conferência PDV", trazido para onde a decisão acontece:
// o Financeiro confirma o recebimento (etapa 4) já vendo se aquele depósito tem
// lastro no caixa, em vez de descobrir depois num painel separado.
//
// ⚠️ O carimbo INFORMA, não trava. O ETL do PDV é D-1: depósito recente ainda não
// pode estar lá — por isso "Aguardando PDV" é neutro (espera), e não acusação.
// Travar a confirmação por causa disso pararia o ciclo do troco todo dia.
//
// Os valores espelham CONFERENCIA do server/financeiro/troco-utils.js.
const CONFERENCIA_CONFIG = {
    Conferido: {
        rotulo: 'Conferido', bg: '#dcfce7', cor: '#15803d', Icone: ShieldCheck,
        titulo: 'O caixa registrou esta banana com o mesmo valor.',
    },
    ValorDivergente: {
        rotulo: 'Valor difere', bg: '#fef3c7', cor: '#b45309', Icone: AlertTriangle,
        titulo: 'A banana existe no caixa, mas com valor diferente do declarado.',
    },
    SemLastro: {
        rotulo: 'Sem lastro', bg: '#fee2e2', cor: '#b91c1c', Icone: ShieldAlert,
        titulo: 'O caixa não tem registro desta banana.',
    },
    AguardandoPdv: {
        rotulo: 'Aguardando PDV', bg: '#f1f5f9', cor: '#475569', Icone: Clock,
        titulo: 'Depósito mais recente que a última carga do caixa — ainda não dá para conferir.',
    },
    DestinoDivergente: {
        rotulo: 'Destino difere', bg: '#ffedd5', cor: '#c2410c', Icone: AlertTriangle,
        titulo: 'Declarado como envio ao escritório, mas o caixa registrou uma banana com o valor exato.',
    },
    // Envio ao escritório não gera banana: não há o que conferir. Marcar como
    // "Conferido" seria elogio indevido; como "Sem lastro", acusação indevida.
    SemConferencia: {
        rotulo: 'Não se aplica', bg: '#f8fafc', cor: '#94a3b8', Icone: MinusCircle,
        titulo: 'Envio ao escritório não gera banana — não há o que conferir no caixa.',
    },
};

export const TrocoConferenciaBadge = ({ conferencia, foraDaConferencia }) => {
    // Sem veredito (troco ainda não depositado, acerto, ou painel não carregado):
    // nada é melhor do que um carimbo inventado.
    if (!conferencia) return null;
    const c = CONFERENCIA_CONFIG[foraDaConferencia ? 'SemConferencia' : conferencia];
    if (!c) return null;
    const { Icone } = c;
    return (
        <span title={c.titulo} style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '3px 8px', borderRadius: '999px',
            backgroundColor: c.bg, color: c.cor,
            fontSize: '10px', fontWeight: '700', textTransform: 'uppercase',
            whiteSpace: 'nowrap',
        }}>
            <Icone size={11} /> {c.rotulo}
        </span>
    );
};

export default TrocoStatusBadge;
